/**
 * Semantic linter rules for PathFollow3D: Godot's configuration warnings
 * (`path_3d.cpp:354-369`) plus the cross-field checks that need the rest of the scene.
 * linterParser.ts does the format validation. The placement rules warn, since a configuration
 * warning is advisory and a misplaced PathFollow3D loads fine (ADR-0032).
 */
import { ruleRegistry } from '../../../linter/RuleRegistry.js';
import { hiddenOrUnknowableInTree, parentTypeVerdict, placementPhrase } from '../../../linter/parentType.js';
import { resolveSubResourceRef } from '../../../resources/SubResourceResolver.js';
import { ruleInt } from '../../../godot/variant/intSlots.js';
import { PathFollow3DRotationMode } from '../../../godot/scene/pathFollow3d.js';
import { boolSlotValue, scalarSlotValue } from '../../../godot/variant/variantBool.js';
import { armEmits, groundedArm, reportArm } from '../../../linter/ruleArms.js';
const arms = {
    noParent: groundedArm('pathfollow3d-no-parent', { kind: 'configuration-warning' }),
    invalidParent: groundedArm('pathfollow3d-invalid-parent', { kind: 'configuration-warning' }),
    negativeProgress: groundedArm('pathfollow3d-negative-progress', {
        kind: 'engine-inert',
        at: 'curve.cpp:2024',
        unused: 'the sampler clamps the offset, so travel before the start moves nothing',
    }),
    progressRatioIgnored: {
        severity: 'error',
        ruleName: 'pathfollow3d-progress-ratio-ignored',
        grounding: { kind: 'engine', at: 'path_3d.cpp:503' },
    },
    orientedModeWithoutUpVector: groundedArm('pathfollow3d-oriented-mode-requires-up-vector', {
        kind: 'configuration-warning',
    }),
};
/**
 * True only when the parent Path3D's curve is in this file and says
 * `up_vector_enabled = false`. False for a curve behind an `ExtResource`, and for an absent
 * key, since the default is `true` (curve.h:299).
 */
function parentCurveDisablesUpVector(scene, parent) {
    const curve = resolveSubResourceRef(parent.rawProperties.curve, scene.internalResources);
    return boolSlotValue(curve?.data.up_vector_enabled) === false;
}
function checkPathFollow3D(context) {
    const diagnostics = [];
    const { node, scene } = context;
    const rawProps = node.rawProperties;
    // path_3d.cpp:357: both of this override's warnings sit inside
    // `is_visible_in_tree() && is_inside_tree()`. The progress and dual-key
    // checks further down are this repo's own and carry no such gate.
    const gated = hiddenOrUnknowableInTree(scene, node);
    // path_3d.cpp:359: the placement Godot flags, at the root too, where the cast is
    // `cast_to<Path3D>(nullptr)`. `parentTypeVerdict` exempts a parent whose type lives in a
    // sub-scene the linter never opens, which may be a Path3D.
    const placement = parentTypeVerdict(scene, node, 'Path3D');
    if (!gated) {
        if (placement.kind === 'root') {
            reportArm(diagnostics, arms.noParent, node, `PathFollow3D '${node.name}' is the scene root. It only works as a direct child of a Path3D node, and follows nothing here.`);
        }
        else if (placement.kind === 'mismatch') {
            reportArm(diagnostics, arms.invalidParent, node, `PathFollow3D '${node.name}' is ${placementPhrase(placement)}. It only works as a direct child of a Path3D node, and follows nothing here.`);
        }
    }
    // The value survives, but the travel it asks for does not. Every
    // sampler clamps the offset into the curve, so the follower parks at the
    // start rather than extrapolating backwards off the end.
    if (rawProps.progress !== undefined) {
        const progress = scalarSlotValue(rawProps.progress);
        // `set_progress` opens with ERR_FAIL_COND(!std::isfinite(p_progress))
        // (path_3d.cpp:450), so a non-finite one never lands and this rule has
        // nothing to say about the travel it would have asked for.
        if (progress !== null && Number.isFinite(progress) && progress < 0) {
            reportArm(diagnostics, arms.negativeProgress, node, `PathFollow3D 'progress' is negative (${progress}). Godot keeps the value, but clamps it when sampling the curve, so the follower sits at the start of the path.`);
        }
    }
    // Not a second spelling of `progress`: `PackedScene::instantiate` sets stored properties
    // (packed_scene.cpp:492) before it parents (:541), and `path` binds on NOTIFICATION_ENTER_TREE.
    // So `set_progress` stores the raw value, and `set_progress_ratio` (path_3d.cpp:503), missing its
    // parent, drops every authored ratio: the check is unconditional, whatever the value.
    if (rawProps.progress_ratio !== undefined) {
        reportArm(diagnostics, arms.progressRatioIgnored, node, `PathFollow3D 'progress_ratio' is set. A scene file cannot carry it: the setter needs a Path3D parent that is already in the tree, and properties are applied before the node is parented, so Godot drops it. Use 'progress' instead.`);
    }
    // path_3d.cpp:362: all three conjuncts, not only the mode. The curve must be
    // readable here and say up vectors are off.
    if (!gated &&
        ruleInt(rawProps.rotation_mode ?? '') === PathFollow3DRotationMode.ORIENTED &&
        placement.kind === 'satisfied' &&
        parentCurveDisablesUpVector(scene, placement.parent)) {
        reportArm(diagnostics, arms.orientedModeWithoutUpVector, node, `PathFollow3D '${node.name}' uses ROTATION_ORIENTED, but its parent Path3D's Curve3D sets 'up_vector_enabled = false'. Godot needs up vectors for that mode.`);
    }
    return diagnostics;
}
const pathFollow3DValidationRule = {
    meta: {
        name: 'valid-pathfollow3d',
        description: 'Validates PathFollow3D parent relationship, progress values, and rotation mode requirements',
        category: 'validation',
        applicableNodeTypes: ['PathFollow3D'],
        emits: armEmits(arms),
    },
    check: checkPathFollow3D,
};
ruleRegistry.register(pathFollow3DValidationRule);
//# sourceMappingURL=linter.js.map